/**
 * ReadingProgress — 阅读进度指示器
 *
 * 功能：
 * - 顶部显示细线进度条，告知用户当前阅读位置
 * - 仅在内容超过屏幕 2 倍高度时显示
 * - 渐变色匹配深海主题
 * - 平滑过渡动画
 * - 支持显示百分比
 *
 * 使用示例：
 * <ReadingProgress showPercentage />
 */

import React, { useState, useEffect, useCallback, memo } from 'react';

// ★ 常量配置
const MIN_CONTENT_HEIGHT = 2000;      // 最小内容高度（px）才显示进度条
const THROTTLE_DELAY = 50;            // 节流延迟（ms）

/**
 * 阅读进度指示器组件
 * @param {Object} props
 * @param {boolean} [props.showPercentage=false] - 是否显示百分比数字
 * @param {string} [props.className=''] - 自定义类名
 */
const ReadingProgress = memo(({ showPercentage = false, className = '' }) => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // ★ 计算阅读进度
  const calculateProgress = useCallback(() => {
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // 内容不足以滚动时不显示
    if (documentHeight < windowHeight * 2 || documentHeight < MIN_CONTENT_HEIGHT) {
      setIsVisible(false);
      return;
    }

    // 计算可滚动区域
    const scrollableHeight = documentHeight - windowHeight;

    // 计算百分比
    const currentProgress = Math.min(100, Math.max(0, (scrollTop / scrollableHeight) * 100));

    setProgress(currentProgress);
    setIsVisible(currentProgress > 2); // 超过 2% 才显示
  }, []);

  // ★ 监听滚动事件
  useEffect(() => {
    let ticking = false;
    let timeoutId = null;

    const handleScroll = () => {
      if (!ticking) {
        timeoutId = setTimeout(() => {
          calculateProgress();
          ticking = false;
        }, THROTTLE_DELAY);
        ticking = true;
      }
    };

    // 初始计算
    calculateProgress();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', calculateProgress, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculateProgress);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [calculateProgress]);

  // 不显示时返回 null
  if (!isVisible) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[60] pointer-events-none ${className}`}
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="阅读进度"
    >
      {/* 进度条容器 */}
      <div className="h-1 bg-slate-800/30">
        {/* 进度条 */}
        <div
          className="h-full transition-all duration-150 ease-out relative"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, rgba(80, 160, 220, 0.8), rgba(120, 200, 255, 0.9), rgba(100, 220, 255, 1))',
            boxShadow: '0 0 12px rgba(80, 180, 230, 0.5), 0 0 4px rgba(100, 200, 255, 0.8)',
          }}
        >
          {/* 光泽效果 */}
          <div
            className="absolute right-0 top-0 h-full w-8"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6))',
            }}
          />
        </div>
      </div>

      {/* 百分比显示（可选） */}
      {showPercentage && (
        <div
          className="absolute top-2 right-4 text-xs font-medium px-2 py-1 rounded-full"
          style={{
            background: 'rgba(8, 18, 36, 0.8)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'rgba(180, 220, 255, 0.9)',
            border: '1px solid rgba(100, 180, 230, 0.15)',
          }}
        >
          {Math.round(progress)}%
        </div>
      )}
    </div>
  );
});

ReadingProgress.displayName = 'ReadingProgress';

export default ReadingProgress;
